import React, { useState } from "react";
import { History, Trash2 } from "lucide-react";

import { Button } from "components/ui/button";

import { LOCAL_DRAFT_MESSAGES } from "../../constants/messages";
import { DiscardConfirmDialog } from "../../wizard/DiscardConfirmDialog";
import type { LocalDraft } from "../hooks/useLocalDraft";

interface LocalDraftBannerProps {
  draft: LocalDraft;
  onRestore: () => void;
  onDiscard: () => void;
}

function formatSavedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const LocalDraftBanner: React.FC<LocalDraftBannerProps> = ({
  draft,
  onRestore,
  onDiscard,
}) => {
  const savedAt = formatSavedAt(draft.savedAt);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  const confirmDiscard = (): void => {
    setConfirmOpen(false);
    onDiscard();
  };

  return (
    <div
      className="-mb-5 flex w-full flex-wrap items-center gap-x-3 gap-y-1.5 self-stretch rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5"
    >
      <History className="h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
      <p className="min-w-0 flex-1 text-sm text-amber-900">
        <span className="font-medium">
          {savedAt
            ? LOCAL_DRAFT_MESSAGES.foundAt(savedAt)
            : LOCAL_DRAFT_MESSAGES.found}
        </span>{" "}
        <span className="text-xs text-amber-800">
          {LOCAL_DRAFT_MESSAGES.scope}
          {draft.missingFiles.length > 0 && (
            <> {LOCAL_DRAFT_MESSAGES.missingFiles(draft.missingFiles)}</>
          )}
          {draft.segmentationOmitted && (
            <> {LOCAL_DRAFT_MESSAGES.segmentationOmitted}</>
          )}
        </span>
      </p>
      <div className="ml-auto flex items-center gap-2">
        <Button
          type="button"
          variant="destructive"
          size="sm"
          className="h-8"
          onClick={() => setConfirmOpen(true)}
        >
          <Trash2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
          {LOCAL_DRAFT_MESSAGES.discard}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8"
          onClick={onRestore}
        >
          {LOCAL_DRAFT_MESSAGES.restore}
        </Button>
      </div>
      <DiscardConfirmDialog
        open={confirmOpen}
        title={LOCAL_DRAFT_MESSAGES.discardTitle}
        description={LOCAL_DRAFT_MESSAGES.discardDescription}
        confirmLabel={LOCAL_DRAFT_MESSAGES.discardConfirm}
        cancelLabel={LOCAL_DRAFT_MESSAGES.discardCancel}
        onConfirm={confirmDiscard}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};
